import { Box, keyframes } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const badgePulse = keyframes`
  0% { transform: scale(1); opacity: 0.65; }
  70% { transform: scale(1.85); opacity: 0; }
  100% { transform: scale(1.85); opacity: 0; }
`;

type NewFeatureBadgeProps = React.ComponentPropsWithoutRef<'span'> & {
  children: React.ReactNode;
};

/**
 * Small "New" pill on the physical top-right of an icon button, with a pulse ring.
 * Position is an inline style so RTL styling does not move it to the other corner.
 */
const NewFeatureBadge = React.forwardRef<HTMLSpanElement, NewFeatureBadgeProps>(
  function NewFeatureBadge({ children, ...rest }, ref) {
    const { t } = useTranslation();

    return (
      <Box
        component="span"
        ref={ref}
        {...rest}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          lineHeight: 0,
          overflow: 'visible',
        }}
      >
        {children}
        <Box
          component="span"
          aria-hidden
          style={{ position: 'absolute', top: -7, right: -8, zIndex: 2 }}
          sx={{ pointerEvents: 'none', display: 'inline-flex' }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: -1,
              borderRadius: '999px',
              backgroundColor: '#e24b4a',
              animation: `${badgePulse} 1.6s ease-out infinite`,
            }}
          />
          <Box
            component="span"
            sx={{
              position: 'relative',
              borderRadius: '999px',
              backgroundColor: '#e24b4a',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: '0.03em',
              px: '4px',
              py: '2px',
              textTransform: 'uppercase',
            }}
          >
            {t('FEATURE_NEW_BADGE')}
          </Box>
        </Box>
      </Box>
    );
  }
);

export default NewFeatureBadge;
